import { z } from "zod";
import { optionalTrimmedString, trimmedString } from "./primitives";

export const subscriptionStatusSchema = z.enum([
  "trialing",
  "active",
  "past_due",
  "canceled",
  "expired",
]);

export const subscriptionAccessSchema = z.enum(["full", "read_only"]);

export const billingStatusSchema = z.object({
  orgId: trimmedString,
  plan: z.literal("standard"),
  priceCents: z.literal(29900),
  currency: z.literal("USD"),
  status: subscriptionStatusSchema,
  access: subscriptionAccessSchema,
  trialStartedAt: trimmedString,
  trialEndsAt: trimmedString,
  daysRemaining: z.number().int().nonnegative(),
  currentPeriodEndsAt: optionalTrimmedString,
  paymentProcessor: z.enum(["mock", "stripe"]),
  trialRetention: z.object({
    retainUntil: z.string().datetime(),
    state: z.enum(["retained", "eligible_for_review"]),
    automaticDeletion: z.literal(false),
  }).optional(),
});

export const createCheckoutSchema = z.object({
  returnPath: optionalTrimmedString.refine(
    (value) => !value || (value.startsWith("/") && !value.startsWith("//")),
    "Return path must be a local application path."
  ),
});

export const checkoutResultSchema = z.object({
  checkoutId: trimmedString,
  redirectUrl: optionalTrimmedString,
  completed: z.boolean(),
  billing: billingStatusSchema,
});

export const confirmCheckoutSchema = z.object({
  checkoutId: trimmedString.refine((value) => value.startsWith("cs_"), "Checkout ID must be valid."),
});

export const createPortalSchema = z.object({
  returnPath: optionalTrimmedString.refine(
    (value) => !value || (value.startsWith("/") && !value.startsWith("//")),
    "Return path must be a local application path."
  ),
});

export const portalResultSchema = z.object({
  redirectUrl: trimmedString.refine((value) => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }, "Redirect URL must be valid."),
});

export type BillingStatus = z.infer<typeof billingStatusSchema>;
export type CreateCheckoutPayload = z.infer<typeof createCheckoutSchema>;
export type CheckoutResult = z.infer<typeof checkoutResultSchema>;
export type ConfirmCheckoutPayload = z.infer<typeof confirmCheckoutSchema>;
export type CreatePortalPayload = z.infer<typeof createPortalSchema>;
export type PortalResult = z.infer<typeof portalResultSchema>;
